/* eslint-disable react/jsx-no-target-blank */
import React from 'react';
import { t, Trans } from '@lingui/macro';

import { Box, Flex, Paragraph } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import HowRoverWorks, { ImageOptions } from '@rover/react-lib/src/components/HowRoverWorks';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import GetDetailedUpdates from '@rover/react-lib/src/pages/landing/ServiceLanding/components/GetDetailedUpdates';
import Guarantees from '@rover/react-lib/src/pages/landing/ServiceLanding/components/Guarantees';
import ServiceLandingServices from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServices';
import ServiceTargetAudience from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceTargetAudience';
import ServiceValueProposition from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceValueProposition';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import ServiceLandingResultsLayout from '../../components/ServiceLandingResultsLayout';

import doggyDayCareHeroImageDesktop from './images/Hero_desktop.jpg';
import doggyDayCareHeroImageMobile from './images/Hero_mobile.jpg';
import doggyDayCareHeroImageTablet from './images/Hero_tablet.jpg';

const heroImages: HeroImageSrcSet = {
  desktop: {
    src: doggyDayCareHeroImageDesktop,
    height: 927,
  },
  tablet: {
    src: doggyDayCareHeroImageTablet,
    height: 927,
  },
  mobile: {
    src: doggyDayCareHeroImageMobile,
    height: 614,
  },
};

const DoggyDayCare: React.FC<Props> = ({
  countryConfig,
  citiesAndNeighborhoods,
  language,
  serviceLandingData,
  serviceLinksWithDisplayName,
  searchResults,
  frontendConfigSettings,
  adwordslocation,
  serviceTypes,
}) => {
  const serviceLandingType = ServiceLandingEnum.DOGGY_DAY_CARE;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero mb="20x" heroImageSrcSet={heroImages}>
        <LandingHero.Content>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
          />
          <LandingHero.TrustPilot languageCode={language} />
        </LandingHero.Content>
      </LandingHero>
      {searchResults && searchResults.results.length && (
        <ServiceLandingResultsLayout
          serviceLandingType={serviceLandingType}
          language={language}
          countryConfig={countryConfig}
          searchResults={searchResults}
          mapSettings={frontendConfigSettings}
          adwordslocation={adwordslocation}
        />
      )}
      <Section mt="0x" mb="40x">
        <Flex alignItems="center" flexDirection="column" data-qa-id="service-landing-description">
          <ServiceValueProposition>
            <ServiceValueProposition.Display>
              <Trans>Finally—a doggy day care option that works</Trans>
            </ServiceValueProposition.Display>
            <ServiceValueProposition.List>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Pet sitters you can trust</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Every sitter profile has been individually reviewed and approved by Rover and
                    sitters on Rover have received millions of 5-star reviews.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Easy scheduling</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Choose the days of the week your dog needs day care. Drop off your dog at your
                    sitter's home in the morning, and pick them up in the evening.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Easy Meet & Greets</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Meet your dog's perfect match before you book. Schedule a free in-person Meet &
                    Greet, right from your Rover inbox.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
            </ServiceValueProposition.List>
          </ServiceValueProposition>
          <HowRoverWorks>
            <HowRoverWorks.Display>
              <Trans>Browse to book in 3 steps flat</Trans>
            </HowRoverWorks.Display>
            <HowRoverWorks.List>
              <HowRoverWorks.ListItem image={ImageOptions.MAGNIFYING_GLASS}>
                <HowRoverWorks.ItemHeading step={1}>
                  <Trans>Search Pet Sitters</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    Browse profiles of nearby sitters who offer doggy day care in their homes.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>

              <HowRoverWorks.ListItem image={ImageOptions.COLLAR}>
                <HowRoverWorks.ItemHeading step={2}>
                  <Trans>Meet In Person</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>Schedule a free in-person Meet & Greet to find the perfect fit.</Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>

              <HowRoverWorks.ListItem image={ImageOptions.PHONE}>
                <HowRoverWorks.ItemHeading step={3}>
                  <Trans>Book and Pay</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    It’s that easy! All payments are processed through Rover's secured platform.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
            </HowRoverWorks.List>
          </HowRoverWorks>

          <GetDetailedUpdates
            headingText={t`Get details from your sitter`}
            introText={t`With the Rover app, get a comprehensive Rover Card from your sitter that includes:`}
            listItems={[
              <Paragraph key="0">
                <Trans comment="part of a list of updates owners can receive from a dog walker">
                  Pee, poo, food, and water breaks
                </Trans>
              </Paragraph>,
              <Paragraph key="1">
                <Trans comment="part of a list of updates owners can receive from a dog walker">
                  Cute photos and a short note
                </Trans>
              </Paragraph>,
              <Paragraph key="2">
                <Trans comment="part of a list of updates owners can receive from a dog walker">
                  When the stay starts and ends
                </Trans>
              </Paragraph>,
            ]}
          />

          <Guarantees serviceTypeSlug={serviceTypeSlug} countryConfig={countryConfig} />

          <ServiceTargetAudience data-qa-id="service-landing-description">
            <ServiceTargetAudience.Display>
              <Trans>Give your dog the best day ever</Trans>
            </ServiceTargetAudience.Display>
            <ServiceTargetAudience.Content>
              <Trans>
                Thousands of sitters on Rover are happy to take care of your dog while you're at
                work or unavailable for the day. Book a one-time visit, or set up a regular rhythm
                with your favorite sitter. Drop off your dog at their sitter's home and know they'll
                get frequent potty breaks, plenty of playtime, and lots of love. Doggy day care is
                great for:
              </Trans>
            </ServiceTargetAudience.Content>
            <ServiceTargetAudience.List>
              <ServiceTargetAudience.ListItem>
                <Trans>Puppies and high-energy dogs</Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>Dogs with special needs, including seniors</Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>Pet parents who work long hours</Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>Dogs with separation anxiety</Trans>
              </ServiceTargetAudience.ListItem>
            </ServiceTargetAudience.List>
          </ServiceTargetAudience>

          {/* Reviews */}
          {/* Big Reviews */}

          <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
            <ServiceLandingServices.Display>
              <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
            </ServiceLandingServices.Display>
          </ServiceLandingServices>

          <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
            <CitiesAndNeighborhoods.Display>
              <Trans>
                Thousands of doggy day care sitters in cities and neighborhoods, including:
              </Trans>
            </CitiesAndNeighborhoods.Display>
          </CitiesAndNeighborhoods>

          <Box my="8x" data-qa-id="service-landing-download-app-callout">
            <AppStoreCallout />
          </Box>
        </Flex>
      </Section>
      =
    </>
  );
};

export default DoggyDayCare;
